/*
 * The device's "reduce motion" setting (`prefers-reduced-motion: reduce`),
 * read in one place (ENG-836) so every consumer agrees and follows it live.
 * Stream effects use it through `useReduceEffects`; the site-wide reduced
 * motion work (ENG-837) is meant to adopt the same helper and its hook,
 * `usePrefersReducedMotion`, rather than add a second `matchMedia` read.
 *
 * It is a per-device fact: nothing here saves it anywhere.
 */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const mediaQuery = () => {
  try {
    return window.matchMedia?.(REDUCED_MOTION_QUERY) ?? null;
  } catch (_e) {
    return null;
  }
};

/** Whether the device asks for reduced motion right now. */
export const prefersReducedMotion = () => Boolean(mediaQuery()?.matches);

/**
 * Calls `fn` whenever the device's reduce-motion setting changes while the
 * page is open. Returns the unsubscribe function. Falls back to the older
 * `addListener` API (Safari < 14).
 */
export const subscribePrefersReducedMotion = (fn) => {
  const mq = mediaQuery();
  if (!mq) return () => {};
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener?.('change', fn);
  }
  if (typeof mq.addListener === 'function') {
    mq.addListener(fn);
    return () => mq.removeListener?.(fn);
  }
  return () => {};
};
